import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";

export function ClientPostersDialog({ open, onOpenChange }) {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(new Set());
    setQ("");
    if (!clients) {
      api.get("/capital/clients")
        .then((r) => setClients(r.data.map((c) => ({ name: c.name, count: c.asset_count }))))
        .catch(() => setClients([]));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const list = clients || [];
    const needle = q.trim().toLowerCase();
    return needle ? list.filter((c) => c.name.toLowerCase().includes(needle)) : list;
  }, [clients, q]);

  const toggle = (name) => {
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };
  const allFilteredPicked = filtered.length > 0 && filtered.every((c) => picked.has(c.name));
  const toggleAllFiltered = () => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (allFilteredPicked) filtered.forEach((c) => next.delete(c.name));
      else filtered.forEach((c) => next.add(c.name));
      return next;
    });
  };

  const generate = async () => {
    if (picked.size === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/site-qr/client-posters.pdf",
        { client_names: Array.from(picked) },
        { responseType: "blob", timeout: 180000 });
      const u = URL.createObjectURL(r.data);
      const el = document.createElement("a");
      el.href = u;
      el.download = `GBE-Client-QR-Posters-${picked.size}-clients.pdf`;
      el.click();
      URL.revokeObjectURL(u);
      onOpenChange(false);
    } catch { toast.error("Batch poster generation failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="client-posters-dialog">
        <DialogHeader>
          <DialogTitle>Batch client QR posters</DialogTitle>
          <DialogDescription>
            Pick clients to generate one PDF with a QR poster per client — ready for mail-outs.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…"
                 className="pl-9 h-9 bg-slate-50" data-testid="client-posters-search" />
        </div>

        {clients === null ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading clients…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-slate-500 px-1">
              <button onClick={toggleAllFiltered} className="text-blue-800 hover:underline" data-testid="client-posters-selectall">
                {allFilteredPicked ? "Clear" : "Select"} all shown ({filtered.length})
              </button>
              <span data-testid="client-posters-count">{picked.size} selected</span>
            </div>
            <div className="max-h-[320px] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {filtered.map((c) => (
                <label key={c.name} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                       data-testid={`client-posters-row-${c.name.replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}`}>
                  <Checkbox checked={picked.has(c.name)} onCheckedChange={() => toggle(c.name)} />
                  <span className="text-sm text-slate-800 flex-1 truncate">{c.name}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">{c.count} assets</span>
                </label>
              ))}
              {filtered.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No clients match.</div>}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={generate} disabled={busy || picked.size === 0} data-testid="client-posters-generate">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
            Generate {picked.size > 0 ? `(${picked.size})` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
